import { toast } from "sonner";
import axiosInstance from "../../utility/axios";
import {
  removeCompany,
  removeFile,
  setDeletingFileId,
  setFiles,
  setSelectedFile,
  setSelectedFileLoading,
  setUpdatingFileId,
  updateFile,
  loadFiles,
  finishLoadingFiles,
  finishLoadingBranches,
  finishLoadingEmployees,
  loadBranches,
  loadEmployees,
  setCreatingEmployeeCompanyId,
  setBranches,
  setCompanies,
  setCompany,
  setCompanyLoading,
  setEmployees,
  setLoadingCompanyId,
  setTotalCompany,
  updateCompanyInList,
} from "../../store/slices/companies";
import { AppDispatch } from "../../store/store";
import {
  ICompany,
  ICompanyBranch,
  ICompanyCount,
  ICompanyEmployeeData,
  ICompanyFilesResponseData,
  ICompanyResponse,
  ICreateCompanyEmployee,
  IEmployee,
  IMutateCompany,
  IMutateCompanyBranch,
  IMutateCompanyBranchResponse,
  IMutateCompanyResponse,
  IUploadCompanyFiles,
  IUploadCompanyFilesResponseData,
  IUpdateCompanyFile,
} from "../../store/types/companies";
import { getErrorMessage } from "../apiError";

const companiesEndpoint = "/api/v1/admin/companies";

type ApiResponse<T> = {
  success: boolean;
  message: string;
  data: T;
};

const requestWithErrorHandler = async <T>(
  fallback: string,
  request: () => Promise<T>,
): Promise<T | null> => {
  try {
    return await request();
  } catch (error) {
    toast.error(getErrorMessage(error, fallback));
    return null;
  }
};

const assertApiSuccess = <T>(
  response: ApiResponse<T>,
  fallback: string,
): void => {
  if (!response.success) {
    throw new Error(response.message || fallback);
  }
};

export const get_companies = async (
  dispatch: AppDispatch,
): Promise<ICompany[] | null> => {
  dispatch(setCompanyLoading(true));
  try {
    const response = await requestWithErrorHandler(
      "Failed to load companies",
      async () => {
        const result =
          await axiosInstance.get<ICompanyResponse>(companiesEndpoint);
        assertApiSuccess(result.data, "Failed to load companies");
        return result.data.data;
      },
    );
    if (response) dispatch(setCompanies(response));
    return response;
  } finally {
    dispatch(setCompanyLoading(false));
  }
};

export const get_company = async (
  id: number,
  dispatch: AppDispatch,
): Promise<ICompany | null> => {
  dispatch(setCompanyLoading(true));
  dispatch(setCompany(null));
  try {
    const response = await requestWithErrorHandler(
      "Failed to load company",
      async () => {
        const result = await axiosInstance.get<ApiResponse<ICompany>>(
          `${companiesEndpoint}/${id}`,
        );
        assertApiSuccess(result.data, "Failed to load company");
        return result.data.data;
      },
    );
    if (response) dispatch(setCompany(response));
    return response;
  } finally {
    dispatch(setCompanyLoading(false));
  }
};

export const create_company = async (
  data: IMutateCompany,
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setCompanyLoading(true));
  try {
    const response = await requestWithErrorHandler(
      "Failed to create company",
      async () => {
        const result = await axiosInstance.post<IMutateCompanyResponse>(
          companiesEndpoint,
          data,
        );
        assertApiSuccess(result.data, "Failed to create company");
        return result.data;
      },
    );
    if (!response) return false;
    toast.success(response.message || "Company created successfully");
    await get_companies(dispatch);
    return true;
  } finally {
    dispatch(setCompanyLoading(false));
  }
};

export const update_company = async (
  id: number,
  data: IMutateCompany,
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setCompanyLoading(true));
  try {
    const response = await requestWithErrorHandler(
      "Failed to update company",
      async () => {
        const result = await axiosInstance.patch<IMutateCompanyResponse>(
          `${companiesEndpoint}/${id}`,
          data,
        );
        assertApiSuccess(result.data, "Failed to update company");
        return result.data;
      },
    );
    if (!response) return false;
    toast.success(response.message || "Company updated successfully");
    await get_companies(dispatch);
    return true;
  } finally {
    dispatch(setCompanyLoading(false));
  }
};

export const delete_company = async (
  id: number,
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setLoadingCompanyId(id));
  try {
    const response = await requestWithErrorHandler(
      "Failed to delete company",
      async () => {
        const result = await axiosInstance.delete<ApiResponse<unknown>>(
          `${companiesEndpoint}/${id}`,
        );
        assertApiSuccess(result.data, "Failed to delete company");
        return result.data;
      },
    );
    if (!response) return false;
    dispatch(removeCompany(id));
    toast.success(response.message || "Company deleted successfully");
    return true;
  } finally {
    dispatch(setLoadingCompanyId(null));
  }
};

export const get_company_branches = async (
  dispatch: AppDispatch,
  companyId: number,
): Promise<boolean> => {
  dispatch(loadBranches(companyId));
  try {
    const response = await requestWithErrorHandler(
      "Failed to load company branches",
      async () => {
        const result = await axiosInstance.get<ApiResponse<ICompanyBranch[]>>(
          `${companiesEndpoint}/${companyId}/branches`,
        );
        assertApiSuccess(result.data, "Failed to load company branches");
        return result.data;
      },
    );
    if (!response) return false;
    dispatch(setBranches({ companyId, branches: response.data }));
    return true;
  } finally {
    dispatch(finishLoadingBranches(companyId));
  }
};

export const get_company_branch_users = async (
  companyId: number,
  dispatch: AppDispatch,
  limit: number = 20,
  offset: number = 0,
): Promise<boolean> => {
  dispatch(loadEmployees(companyId));
  try {
    const response = await requestWithErrorHandler(
      "Failed to load company branch users",
      async () => {
        const result = await axiosInstance.get<
          ApiResponse<ICompanyEmployeeData>
        >(`${companiesEndpoint}/${companyId}/employees`, {
          params: { limit, offset },
        });
        assertApiSuccess(result.data, "Failed to load company branch users");
        return result.data;
      },
    );
    if (!response) return false;
    dispatch(setEmployees({ companyId, employees: response.data }));
    return true;
  } finally {
    dispatch(finishLoadingEmployees(companyId));
  }
};

export const create_company_branch_user = async (
  dispatch: AppDispatch,
  data: ICreateCompanyEmployee,
  companyId: number,
): Promise<boolean> => {
  dispatch(setCreatingEmployeeCompanyId(companyId));
  try {
    const response = await requestWithErrorHandler(
      "Failed to create company branch user",
      async () => {
        const result = await axiosInstance.post<ApiResponse<IEmployee>>(
          `${companiesEndpoint}/${companyId}/employees`,
          data,
        );
        assertApiSuccess(result.data, "Failed to create company branch user");
        return result.data;
      },
    );
    if (!response) return false;

    toast.success(response.message || "Company employee created successfully");
    await get_company_branch_users(companyId, dispatch);
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to create company branch user"));
    return false;
  } finally {
    dispatch(setCreatingEmployeeCompanyId(null));
  }
};

// uploading files for a single company
export const upload_company_files = async (
  dispatch: AppDispatch,
  data: IUploadCompanyFiles,
  companyId: number,
) => {
  dispatch(setCompanyLoading(true));
  try {
    const response = await requestWithErrorHandler(
      "Failed to upload company files",
      async () => {
        const result = await axiosInstance.post<
          ApiResponse<IUploadCompanyFilesResponseData>
        >(`${companiesEndpoint}/${companyId}/documents`, data);
        assertApiSuccess(result.data, "Failed to upload company files");
        return result.data;
      },
    );
    if (!response) return false;
    toast.success(response.message || "File uploaded successfully");
    return true;
  } finally {
    dispatch(setCompanyLoading(false));
  }
};

export const get_company_files = async (
  dispatch: AppDispatch,
  companyId: number,
  limit: number = 10,
  offset: number = 0,
): Promise<ICompanyFilesResponseData | null> => {
  dispatch(loadFiles({ companyId, limit, offset }));
  try {
    const response = await requestWithErrorHandler(
      "Failed to get company files",
      async () => {
        const result = await axiosInstance.get<
          ApiResponse<ICompanyFilesResponseData>
        >(`${companiesEndpoint}/${companyId}/documents`, {
          params: { limit, offset },
        });
        assertApiSuccess(result.data, "Failed to get company files");
        return result.data;
      },
    );
    if (!response) return null;
    dispatch(setFiles({ companyId, page: response.data }));
    return response.data;
  } finally {
    dispatch(finishLoadingFiles(companyId));
  }
};

export const get_company_file = async (
  dispatch: AppDispatch,
  companyId: number,
  documentId: number,
): Promise<IUploadCompanyFilesResponseData | null> => {
  dispatch(setSelectedFile(null));
  dispatch(setSelectedFileLoading(true));
  try {
    const response = await requestWithErrorHandler(
      "Failed to get company file",
      async () => {
        const result = await axiosInstance.get<
          ApiResponse<IUploadCompanyFilesResponseData>
        >(`${companiesEndpoint}/${companyId}/documents/${documentId}`);
        assertApiSuccess(result.data, "Failed to get company file");
        return result.data;
      },
    );
    if (!response) return null;
    dispatch(setSelectedFile(response.data));
    return response.data;
  } finally {
    dispatch(setSelectedFileLoading(false));
  }
};

export const update_company_file = async (
  dispatch: AppDispatch,
  companyId: number,
  documentId: number,
  data: IUpdateCompanyFile,
): Promise<boolean> => {
  dispatch(setUpdatingFileId(documentId));
  try {
    const response = await requestWithErrorHandler(
      "Failed to update company file",
      async () => {
        const result = await axiosInstance.patch<
          ApiResponse<IUploadCompanyFilesResponseData>
        >(`${companiesEndpoint}/${companyId}/documents/${documentId}`, data);
        assertApiSuccess(result.data, "Failed to update company file");
        return result.data;
      },
    );
    if (!response) return false;
    dispatch(updateFile({ companyId, file: response.data }));
    toast.success(response.message || "Company file updated");
    return true;
  } finally {
    dispatch(setUpdatingFileId(null));
  }
};

export const delete_company_file = async (
  dispatch: AppDispatch,
  companyId: number,
  documentId: number,
): Promise<boolean> => {
  dispatch(setDeletingFileId(documentId));
  try {
    const response = await requestWithErrorHandler(
      "Failed to delete company file",
      async () => {
        const result = await axiosInstance.delete<ApiResponse<unknown>>(
          `${companiesEndpoint}/${companyId}/documents/${documentId}`,
        );
        assertApiSuccess(result.data, "Failed to delete company file");
        return result.data;
      },
    );
    if (!response) return false;
    dispatch(removeFile({ companyId, fileId: documentId }));
    toast.success(response.message || "Company file deleted");
    return true;
  } finally {
    dispatch(setDeletingFileId(null));
  }
};

export const updateCompanyStatus = async (
  id: number,
  isActive: boolean,
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setLoadingCompanyId(id));
  try {
    const response = await requestWithErrorHandler(
      "Failed to update company status",
      async () => {
        const result = await axiosInstance.patch<
          ApiResponse<Partial<ICompany>>
        >(`${companiesEndpoint}/${id}/status`, { is_active: isActive });
        assertApiSuccess(result.data, "Failed to update company status");
        return result.data;
      },
    );
    if (!response) return false;
    dispatch(
      updateCompanyInList({ ...response.data, id, is_active: isActive }),
    );
    toast.success(response.message || "Company status updated");
    return true;
  } finally {
    dispatch(setLoadingCompanyId(null));
  }
};

export const updateCompanySubscription = async (
  id: number,
  isElite: boolean,
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setLoadingCompanyId(id));
  try {
    const response = await requestWithErrorHandler(
      "Failed to update company subscription",
      async () => {
        const result = await axiosInstance.patch<
          ApiResponse<Partial<ICompany>>
        >(`${companiesEndpoint}/${id}/subscription`, { is_elite: isElite });
        assertApiSuccess(result.data, "Failed to update company subscription");
        return result.data;
      },
    );
    if (!response) return false;
    dispatch(updateCompanyInList({ ...response.data, id, is_elite: isElite }));
    toast.success(response.message || "Company subscription updated");
    return true;
  } finally {
    dispatch(setLoadingCompanyId(null));
  }
};

export const createCompanyBranch = async (
  companyId: number,
  data: IMutateCompanyBranch,
): Promise<ICompanyBranch | null> => {
  const response = await requestWithErrorHandler(
    "Failed to create company branch",
    async () => {
      const result = await axiosInstance.post<IMutateCompanyBranchResponse>(
        `${companiesEndpoint}/${companyId}/branches`,
        data,
      );
      assertApiSuccess(result.data, "Failed to create company branch");
      return result.data;
    },
  );
  if (!response) return null;
  toast.success(response.message || "Company branch created");
  return response.data;
};

export const updateCompanyBranch = async (
  companyId: number,
  branchId: number,
  data: IMutateCompanyBranch,
): Promise<ICompanyBranch | null> => {
  const response = await requestWithErrorHandler(
    "Failed to update company branch",
    async () => {
      const result = await axiosInstance.put<IMutateCompanyBranchResponse>(
        `${companiesEndpoint}/${companyId}/branches/${branchId}`,
        data,
      );
      assertApiSuccess(result.data, "Failed to update company branch");
      return result.data;
    },
  );
  if (!response) return null;
  toast.success(response.message || "Company branch updated");
  return response.data;
};

export const deleteCompanyBranch = async (
  companyId: number,
  branchId: number,
): Promise<boolean> => {
  const response = await requestWithErrorHandler(
    "Failed to delete company branch",
    async () => {
      const result = await axiosInstance.delete<ApiResponse<unknown>>(
        `${companiesEndpoint}/${companyId}/branches/${branchId}`,
      );
      assertApiSuccess(result.data, "Failed to delete company branch");
      return result.data;
    },
  );
  if (!response) return false;
  toast.success(response.message || "Company branch deleted");
  return true;
};

// Company analytics
export const get_total_company = async (
  dispatch: AppDispatch,
): Promise<void> => {
  dispatch(setCompanyLoading(true));
  try {
    const response = await axiosInstance.get<ICompanyCount>(
      `${companiesEndpoint}/analytics`,
    );
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to load company count");
    }
    dispatch(setTotalCompany(response.data.data));
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load training total"));
  } finally {
    dispatch(setCompanyLoading(false));
  }
};
